import styled from 'styled-components';
import { useDrfqRfq } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import { isDatedRfqLegs } from '@paradigm/features/src/unified-rfqs/ui/rfqs/legs-functions';
import { useDrfqMakerOrders } from '@paradigm/features/src/unified-rfqs/ui/drfq/drfq-maker-orderbook-context';
import { ArrowDown } from '#/components/rfq-common/TableRow';
import { QtyButton } from '#/components/rfq-common/ActionButtons';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import { useRfqExpansionState } from '#/components/UnifiedRfqs/rfqs/rfq-expansion-context';
import { ONE_CLICK_ACTION_WIDTH } from '#/components/UnifiedRfqs/components/RfqRowBase';
import { BaseRfqAction } from '#/components/UnifiedRfqs/rfqs/action/BaseRfqAction';
import {
  Divider,
  QuoteActionContainer,
} from '#/components/UnifiedRfqs/drfq/common';
import { MakerPriceButton } from '#/components/UnifiedRfqs/drfq/OrderPriceButton';
import {
  BuyContainer,
  ScreenPrices,
  SellContainer,
} from '#/components/UnifiedRfqs/drfq/Orderbook';
import KillOrderButton from '#/components/UnifiedRfqs/drfq/KillOrderButton';
import { MakerRepriceRowAction } from '#/components/UnifiedRfqs/drfq/RepriceRowAction';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import {
  useUMPlatformCreationDisabled,
  useUMPlatformKillDisabled,
} from '@paradigm/features/src/platform-state/contexts/providers';
import { useBboView } from '@paradigm/features/src/unified-rfqs/ui/bbo-view-context';
import TakerDesk from './TakerDesk';

export default function DrfqMakerRowAction() {
  const isPlatformKillDisabled = useUMPlatformKillDisabled();
  const isPlatformCreationDisabled = useUMPlatformCreationDisabled();
  const { state, bid, ask } = useDrfqMakerOrders();
  const rfq = useDrfqRfq();
  const mark = useBboView().markPrice.formatted;
  const isMobile = useIsMobile();
  const isExpanded = useRfqExpansionState(rfq.id);
  const productCode = rfq.product_codes[0];
  const isDated = isDatedRfqLegs(rfq.legs, productCode);
  const bidKill = bid && (
    <KillOrderButton disabled={isPlatformKillDisabled} orderId={bid.id} />
  );
  const askKill = ask && (
    <KillOrderButton disabled={isPlatformKillDisabled} orderId={ask.id} />
  );

  const quantityButton = (
    <QtyButton
      disabled={isPlatformCreationDisabled}
      quantity={rfq.quantity}
      productCode={productCode}
      isNormalized
      isDated={isDated}
    />
  );
  const content =
    state === 'CLOSED_AUCTION' ? (
      <MakerRepriceRowAction />
    ) : (
      <QuoteActionContainer>
        <SellContainer>
          <TakerDesk data-testid="desk-sell" anonDisplayType="taker-profile" />
          {isMobile && <span />}
          {isMobile && <span />}
          <span />
        </SellContainer>
        <BaseRfqAction
          bidQty={quantityButton}
          askQty={quantityButton}
          mark={mark}
          bidPrice={
            <MakerPriceButton
              disabled={isPlatformCreationDisabled}
              mark={mark}
              side="BUY"
              order={bid}
            />
          }
          askPrice={
            <MakerPriceButton
              disabled={isPlatformCreationDisabled}
              mark={mark}
              side="SELL"
              order={ask}
            />
          }
          bidKill={bidKill}
          askKill={askKill}
        />
        <BuyContainer>
          <span />
          {isMobile && <span />}
          {isMobile && <span />}
          <TakerDesk data-testid="desk-buy" anonDisplayType="anon-icon" />
        </BuyContainer>
      </QuoteActionContainer>
    );

  return (
    <EmptyQuoteContainer>
      {content}
      {isExpanded && !isMobile && (
        <>
          <Divider />
          <EmptyQuote />
          <Divider />
          <EmptyQuote />
          <ScreenPrices />
        </>
      )}
      <ArrowDown actionWidth={ONE_CLICK_ACTION_WIDTH} />
    </EmptyQuoteContainer>
  );
}

function EmptyQuote() {
  const isPlatformCreationDisabled = useUMPlatformCreationDisabled();
  return (
    <QuoteActionContainer>
      <span />
      <BaseRfqAction
        bidPrice={
          <MakerPriceButton
            disabled={isPlatformCreationDisabled}
            mark={null}
            side="BUY"
            hidePlaceholder
          />
        }
        askPrice={
          <MakerPriceButton
            disabled={isPlatformCreationDisabled}
            mark={null}
            side="SELL"
            hidePlaceholder
          />
        }
      />
      <span />
    </QuoteActionContainer>
  );
}

const EmptyQuoteContainer = styled.div`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: 100%;
  }
`;
